import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { z } from "zod";
import { CreditCard, History, Inbox, ShieldCheck, Sparkles } from "lucide-react";
import { api, ApiError, okSchema } from "@/lib/api";
import { normalizeUrgency, URGENCY_META } from "@/lib/urgency";
import { PageHeader } from "@/components/layout/AppShell";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Badge, UrgencyBadge } from "@/components/ui/Badge";
import { Button, LinkButton } from "@/components/ui/Button";
import { Input } from "@/components/ui/Field";
import { Alert, EmptyState, LoadingPanel, UrgencyPanel } from "@/components/ui/Feedback";
import { RichText } from "@/components/RichText";
import { useToast } from "@/components/ui/toast";
import { formatDate } from "@/components/ui/DataTable";
import { useSession, isPatient } from "@/lib/session";
import { cn } from "@/lib/cn";

function ScreenError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <Alert tone="danger" title="Gagal memuat data" className="mb-5">
      {error instanceof ApiError ? error.message : "Terjadi kesalahan tak terduga."}
    </Alert>
  );
}

// --------------------------------------------------------------------------
// Admin profile
// --------------------------------------------------------------------------

const profileSchema = z.object({
  user: z.looseObject({
    name: z.string(),
    username: z.string(),
    email: z.string().nullable().default(null),
    role: z.string(),
  }),
  recent_activity: z
    .array(
      z.looseObject({
        action: z.string(),
        target: z.string().nullable().default(null),
        created_at: z.string().nullable().default(null),
      }),
    )
    .default([]),
});

export function AdminProfile() {
  const { data, isPending, error } = useQuery({
    queryKey: ["admin", "profile"],
    queryFn: ({ signal }) => api.get("/api/admin/profile", profileSchema, signal),
  });

  return (
    <>
      <PageHeader
        eyebrow="Akun"
        title="Profil Admin"
        description="Identitas akun Anda dan jejak aktivitas terakhir."
      />
      <ScreenError error={error} />

      {isPending ? (
        <Card>
          <LoadingPanel />
        </Card>
      ) : data ? (
        <div className="grid gap-4 lg:grid-cols-3">
          <Card>
            <CardHeader eyebrow="Identitas" title="Data akun" />
            <CardBody className="space-y-2.5 text-sm">
              {[
                ["Nama", data.user.name],
                ["Username", data.user.username],
                ["Email", data.user.email ?? "—"],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between gap-3">
                  <span className="text-content-subtle shrink-0">{label}</span>
                  <span className="min-w-0 truncate text-right font-medium">{value}</span>
                </div>
              ))}
              <div className="flex justify-between gap-3">
                <span className="text-content-subtle shrink-0">Peran</span>
                <Badge tone={data.user.role === "superadmin" ? "accent" : "neutral"}>
                  {data.user.role === "superadmin" ? "Super admin" : "Admin"}
                </Badge>
              </div>
              <p className="text-content-subtle border-hairline mt-2 border-t pt-2.5 text-xs">
                Perubahan nama, email, dan password akun staf dilakukan oleh super admin melalui
                menu Pengguna.
              </p>
            </CardBody>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader
              eyebrow="Audit trail"
              title="Aktivitas terakhir"
              description="Setiap tindakan admin tercatat dan tidak dapat dihapus dari sini."
            />
            {data.recent_activity.length === 0 ? (
              <EmptyState icon={<History className="size-5" />} title="Belum ada aktivitas" />
            ) : (
              <ul className="divide-hairline divide-y">
                {data.recent_activity.map((item, index) => (
                  <li key={index} className="flex items-center gap-3 px-5 py-2.5">
                    <Badge>{item.action}</Badge>
                    <span className="text-content-muted min-w-0 flex-1 truncate text-xs">
                      {item.target ?? "—"}
                    </span>
                    <span className="text-content-subtle shrink-0 text-xs">
                      {formatDate(item.created_at, true)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      ) : null}
    </>
  );
}

// --------------------------------------------------------------------------
// Admin: one patient's monitoring history
// --------------------------------------------------------------------------

export function AdminHealthPatient() {
  const { rm } = useParams<{ rm: string }>();

  // Reuses the patient-facing history endpoint is not possible here — it is
  // scoped to the signed-in patient — so the admin view reads the monitoring
  // list filtered to this record instead.
  const { data, isPending, error } = useQuery({
    queryKey: ["admin", "health-patient", rm],
    queryFn: ({ signal }) =>
      api.get(
        `/api/admin/health-monitoring?status=all`,
        z.looseObject({
          alerts: z
            .array(
              z.looseObject({
                id: z.number(),
                patient_rm: z.string(),
                patient_name: z.string().nullable().default(null),
                session_uuid: z.string().nullable().default(null),
                severity: z.string(),
                title: z.string(),
                message: z.string(),
                status: z.string(),
                created_at: z.string(),
              }),
            )
            .default([]),
        }),
        signal,
      ),
    enabled: Boolean(rm),
  });

  const alerts = (data?.alerts ?? []).filter((item) => item.patient_rm === rm);
  const patientName = alerts[0]?.patient_name ?? rm;

  return (
    <>
      <PageHeader
        eyebrow={`Rekam medis ${rm}`}
        title={patientName ?? "Detail pasien"}
        description="Seluruh alert kesehatan yang pernah dihasilkan pasien ini."
        actions={
          <LinkButton to={`/admin/patients/${rm}/labs`} size="sm">
            Hasil lab
          </LinkButton>
        }
      />
      <ScreenError error={error} />

      <Card>
        <CardHeader eyebrow="Riwayat" title={`${alerts.length} alert`} />
        {isPending ? (
          <LoadingPanel />
        ) : alerts.length === 0 ? (
          <EmptyState
            icon={<Inbox className="size-5" />}
            title="Belum ada alert"
            description="Pasien ini belum menghasilkan alert kesehatan."
          />
        ) : (
          <ul className="divide-hairline divide-y">
            {alerts.map((alert) => {
              const level = normalizeUrgency(alert.severity);
              return (
                <li key={alert.id} className="flex items-start gap-3 px-5 py-3.5">
                  <span
                    aria-hidden
                    className={cn("mt-1.5 size-2 shrink-0 rounded-full", URGENCY_META[level].dot)}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <UrgencyBadge level={level} />
                      <span className="text-content-subtle text-xs">
                        {formatDate(alert.created_at, true)}
                      </span>
                      <Badge>{alert.status}</Badge>
                    </div>
                    <p className="mt-1 text-sm font-medium">{alert.title}</p>
                    <p className="text-content-muted text-xs">{alert.message}</p>
                  </div>
                  {alert.session_uuid && (
                    <Link
                      to={`/admin/health-monitoring/session/${alert.session_uuid}`}
                      className="text-accent shrink-0 self-center text-xs font-bold hover:underline"
                    >
                      Sesi
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </>
  );
}

const sessionDetailSchema = z.looseObject({
  session_uuid: z.string(),
  status: z.string(),
  check_type: z.string(),
  overall_urgency: z.string().nullable().default(null),
  summary: z.string().nullable().default(null),
  started_at: z.string().nullable().default(null),
  completed_at: z.string().nullable().default(null),
  patient: z
    .looseObject({
      rm_number: z.string(),
      name: z.string(),
      dob: z.string().nullable().default(null),
      gender: z.string().nullable().default(null),
    })
    .nullable()
    .default(null),
  measurements: z
    .array(
      z.looseObject({
        id: z.number(),
        metric_type: z.string(),
        numeric_value: z.number().nullable().default(null),
        unit: z.string().nullable().default(null),
        source_type: z.string().nullable().default(null),
        is_abnormal: z.number().default(0),
      }),
    )
    .default([]),
  symptoms: z
    .array(
      z.looseObject({
        id: z.number(),
        symptom_name: z.string(),
        severity: z.string().nullable().default(null),
      }),
    )
    .default([]),
  alerts: z
    .array(
      z.looseObject({
        id: z.number(),
        severity: z.string(),
        title: z.string(),
        message: z.string(),
        status: z.string(),
        created_at: z.string().nullable().default(null),
      }),
    )
    .default([]),
  clinician_notes: z
    .array(
      z.looseObject({
        id: z.number(),
        author: z.string(),
        note: z.string(),
        created_at: z.string().nullable().default(null),
      }),
    )
    .default([]),
  assessment: z
    .looseObject({
      explanation: z.string().nullable().default(null),
      model: z.string().nullable().default(null),
      success: z.number().default(0),
    })
    .nullable()
    .default(null),
});

/** One monitoring session, in full. Replaces the old server-rendered page. */
export function AdminHealthSession() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [note, setNote] = useState("");

  const { data, isPending, error } = useQuery({
    queryKey: ["admin", "health-session", id],
    queryFn: ({ signal }) =>
      api.get(`/api/admin/health-monitoring/session/${id}`, sessionDetailSchema, signal),
    enabled: Boolean(id),
  });

  const addNote = useMutation({
    mutationFn: () =>
      api.post(`/api/admin/health-monitoring/session/${id}/note`, okSchema, { note }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "health-session", id] });
      setNote("");
      toast.success({ title: "Catatan klinisi tersimpan" });
    },
    onError: (cause) =>
      toast.danger({
        title: "Gagal menyimpan catatan",
        description: cause instanceof ApiError ? cause.message : undefined,
      }),
  });

  if (isPending) {
    return (
      <>
        <PageHeader title="Sesi pemeriksaan" />
        <Card>
          <LoadingPanel />
        </Card>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <PageHeader title="Sesi pemeriksaan" />
        <Alert tone="danger" title="Gagal memuat sesi">
          {error instanceof ApiError ? error.message : "Sesi tidak ditemukan."}
        </Alert>
      </>
    );
  }

  const urgency = normalizeUrgency(data.overall_urgency);

  return (
    <>
      <PageHeader
        eyebrow={data.patient ? `${data.patient.rm_number} · ${data.patient.name}` : "Sesi"}
        title="Sesi pemeriksaan"
        description={`${data.check_type} · ${formatDate(data.completed_at ?? data.started_at, true)}`}
        actions={
          <a
            href={`/admin/health-monitoring/session/${id}/fhir`}
            className="border-hairline-strong bg-surface hover:bg-surface-raised inline-flex h-10 items-center rounded-(--radius-control) border px-5 text-sm font-semibold"
          >
            Ekspor FHIR
          </a>
        }
      />

      <UrgencyPanel level={urgency} className="mb-5">
        {data.summary && <p>{data.summary}</p>}
      </UrgencyPanel>

      <div className="grid gap-4 lg:grid-cols-5">
        <div className="space-y-4 lg:col-span-3">
          {data.assessment?.explanation && (
            <Card>
              <CardHeader
                eyebrow="Penjelasan AI"
                title="Analisis"
                action={
                  data.assessment.model ? <Badge>{data.assessment.model}</Badge> : undefined
                }
              />
              <CardBody>
                <RichText
                  text={data.assessment.explanation}
                  className="text-sm leading-relaxed"
                />
              </CardBody>
            </Card>
          )}

          {data.alerts.length > 0 && (
            <Card>
              <CardHeader eyebrow="Alert" title="Yang dipicu sesi ini" />
              <ul className="divide-hairline divide-y">
                {data.alerts.map((alert) => (
                  <li key={alert.id} className="px-5 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold">{alert.title}</p>
                        <p className="text-content-muted text-xs">{alert.message}</p>
                      </div>
                      <UrgencyBadge level={normalizeUrgency(alert.severity)} />
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <Card>
            <CardHeader
              eyebrow="Klinisi"
              title="Catatan tindak lanjut"
              description="Tercatat permanen pada rekam sesi ini."
            />
            <CardBody className="space-y-3">
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  if (note.trim()) addNote.mutate();
                }}
                className="flex gap-2"
              >
                <Input
                  placeholder="Tulis catatan klinisi…"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  className="flex-1"
                  aria-label="Catatan klinisi"
                />
                <Button
                  type="submit"
                  variant="primary"
                  loading={addNote.isPending}
                  disabled={!note.trim()}
                >
                  Simpan
                </Button>
              </form>

              {data.clinician_notes.length === 0 ? (
                <p className="text-content-subtle text-sm">Belum ada catatan.</p>
              ) : (
                <ul className="divide-hairline divide-y">
                  {data.clinician_notes.map((item) => (
                    <li key={item.id} className="py-2.5">
                      <p className="text-sm">{item.note}</p>
                      <p className="text-content-subtle mt-0.5 text-xs">
                        {item.author} · {formatDate(item.created_at, true)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </div>

        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardHeader eyebrow="Data terukur" title="Tanda vital" />
            {data.measurements.length === 0 ? (
              <EmptyState icon={<Inbox className="size-5" />} title="Tidak ada pengukuran" />
            ) : (
              <ul className="divide-hairline divide-y">
                {data.measurements.map((item) => (
                  <li key={item.id} className="px-5 py-3">
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="text-sm font-medium">{item.metric_type}</p>
                      <span
                        className={cn(
                          "tabular shrink-0 text-sm font-bold",
                          item.is_abnormal ? "text-urgent-text" : "text-content",
                        )}
                      >
                        {item.numeric_value ?? "—"}
                        <span className="text-content-subtle ml-1 text-xs font-medium">
                          {item.unit}
                        </span>
                      </span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-2">
                      <span className="text-content-subtle text-xs capitalize">
                        {item.source_type ?? "manual"}
                      </span>
                      {item.is_abnormal === 1 && <Badge tone="urgent">Di luar normal</Badge>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {data.symptoms.length > 0 && (
            <Card>
              <CardHeader eyebrow="Dilaporkan" title="Gejala" />
              <CardBody className="flex flex-wrap gap-2">
                {data.symptoms.map((symptom) => (
                  <Badge
                    key={symptom.id}
                    tone={symptom.severity === "severe" ? "critical" : "neutral"}
                  >
                    {symptom.symptom_name}
                  </Badge>
                ))}
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}

// --------------------------------------------------------------------------
// Patient subscription
// --------------------------------------------------------------------------

const PLANS = [
  {
    code: "free",
    name: "Gratis",
    price: "Rp0",
    period: "selamanya",
    perks: ["Chat terbatas per hari", "Triase gejala", "Reservasi rawat jalan"],
  },
  {
    code: "hemat",
    name: "Mediora Hemat",
    price: "Rp29.000",
    period: "per bulan",
    perks: [
      "Kuota chat lebih besar",
      "AI Health Check tanpa batas harian",
      "MRI Assist (5 kredit per analisis)",
      "Laporan PDF prioritas",
    ],
    featured: true,
  },
];

export function PatientSubscription() {
  const { data: session } = useSession();
  const patient = isPatient(session) ? session : null;
  const currentPlan = patient?.plan_code ?? "free";

  return (
    <>
      <PageHeader
        eyebrow="Akun"
        title="Langganan"
        description="Kuota AI Anda dan pilihan paket yang tersedia."
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <Card>
          <CardBody>
            <p className="eyebrow mb-1">Paket aktif</p>
            <p className="font-display text-2xl font-extrabold capitalize">{currentPlan}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="eyebrow mb-1">Kredit tersisa</p>
            <p className="tabular font-display text-2xl font-extrabold">
              {patient?.credits_remaining ?? "—"}
              {patient?.credits_quota != null && (
                <span className="text-content-subtle text-base font-semibold">
                  {" "}
                  / {patient.credits_quota}
                </span>
              )}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="eyebrow mb-1">Biaya MRI Assist</p>
            <p className="font-display text-2xl font-extrabold">5 kredit</p>
          </CardBody>
        </Card>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {PLANS.map((plan) => {
          const active = plan.code === currentPlan;
          return (
            <Card
              key={plan.code}
              className={cn(plan.featured && !active && "ring-accent/40 ring-1")}
            >
              <CardHeader
                eyebrow={plan.featured ? "Direkomendasikan" : "Dasar"}
                title={plan.name}
                action={active ? <Badge tone="routine">Aktif</Badge> : undefined}
              />
              <CardBody className="space-y-4">
                <p>
                  <span className="font-display text-3xl font-extrabold">{plan.price}</span>{" "}
                  <span className="text-content-subtle text-sm">{plan.period}</span>
                </p>
                <ul className="space-y-1.5 text-sm">
                  {plan.perks.map((perk) => (
                    <li key={perk} className="flex gap-2">
                      <Sparkles aria-hidden className="text-accent mt-0.5 size-3.5 shrink-0" />
                      {perk}
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          );
        })}
      </div>

      <Alert tone="info" className="mt-4" title="Pembayaran belum tersedia di tampilan baru">
        Aktivasi paket berbayar masih diproses manual oleh tim RS Mediora. Hubungi loket
        pendaftaran atau tim layanan pelanggan untuk mengaktifkan Mediora Hemat.
      </Alert>

      <p className="text-content-subtle mt-4 flex items-start gap-2 text-xs">
        <ShieldCheck aria-hidden className="mt-px size-3.5 shrink-0" />
        <span>
          Mediora tidak pernah meminta data kartu atau PIN melalui chat. Semua pembayaran
          dilakukan langsung di rumah sakit.
        </span>
      </p>

      <div className="mt-5">
        <LinkButton to="/patient/dashboard" icon={<CreditCard className="size-4" />}>
          Kembali ke beranda
        </LinkButton>
      </div>
    </>
  );
}
